import styled, { css } from 'styled-components';
import { useState, useEffect, useContext, RefObject, useReducer, useRef, useCallback, KeyboardEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ResizeObserver from 'resize-observer-polyfill';

import { Icon, IconPropType } from '@/ui/Icon';
import { CourseNavigationContext } from '@/administration/pages/Course/MainLayoutView/atoms/NavigationActions/CourseNavigationProvider';

const KeyboardFocusCSS = css`
  &:focus-visible {
    outline: none;
    box-shadow:
      inset 0 0 0 4px ${(p) => p.theme.colors.primary.outline},
      inset 0 0 0 6px ${(p) => p.theme.colors.primary.onMain},
      0 0.6px 1.8px 0 rgba(0, 0, 0, 0.11),
      0 3.2px 7.2px 0 rgba(0, 0, 0, 0.13);
  }
`;

const RightButtonStyled = styled.div`
  display: flex;
  position: fixed;
  padding: 16px;
  bottom: 16px;
  right: 20px;
  height: 56px;
  min-width: 80px;
  cursor: pointer;
  align-items: center;
  border-radius: 1000px;
  gap: 8px;
  background-color: ${(p) => p.theme.tones.primary[95]};
  box-shadow:
    0 0.6px 1.8px 0 rgba(0, 0, 0, 0.11),
    0 3.2px 7.2px 0 rgba(0, 0, 0, 0.13);

  ${KeyboardFocusCSS};
`;

const IconStyled = styled(Icon)`
  color: ${(p) => p.theme.colors.neutral.onContainer};
`;

const LabelStyled = styled.p`
  color: ${(p) => p.theme.colors.neutral.onContainer};
  ${({ theme }) => theme.typography.body4};
  white-space: nowrap;
  line-height: 1;
`;

const LeftButtonStyled = styled.div<{ $shiftX: number }>`
  width: 56px;
  height: 56px;
  background-color: ${(p) => p.theme.tones.neutralVariant[99]};
  box-shadow:
    0 0.6px 1.8px 0 rgba(0, 0, 0, 0.11),
    0 3.2px 7.2px 0 rgba(0, 0, 0, 0.13);
  border-radius: 1000px;
  display: flex;
  position: fixed;
  align-items: center;
  justify-content: center;
  left: 20px;
  bottom: 16px;
  cursor: pointer;
  transform: translateX(${(p) => p.$shiftX}px);
  transition: transform 400ms ease-in-out;

  ${KeyboardFocusCSS};
`;

type EnhancedCourseNavigationProps = {
  backDisabled?: boolean;
  onBackClick: () => void;
  nextHidden?: boolean;
  onNextClick: () => void;
  editorContainerRef: RefObject<HTMLDivElement>;
  isDrawerOpen?: boolean;
  activePageId?: string | null;
};

// IFrame videos might have not integer height, thats why we need to make this adjustment
// when we track if the page is scrolled to the bottom
const VIDEO_HEIGHT_ADJUSTMENT = 1;

type EditorScrollMetrics = {
  isScrollable: boolean;
  isScrolledToBottom: boolean;
};

// Returns null while there is nothing to measure - an empty container reports a zero
// height and would otherwise look like a fully read page.
const getEditorScrollMetrics = (container: HTMLDivElement | null | undefined, scrollTop: number): EditorScrollMetrics | null => {
  if (!container || container.scrollHeight === 0 || container.clientHeight === 0) {
    return null;
  }

  const { scrollHeight, clientHeight } = container;

  return {
    isScrollable: scrollHeight - VIDEO_HEIGHT_ADJUSTMENT > clientHeight,
    isScrolledToBottom: scrollHeight - VIDEO_HEIGHT_ADJUSTMENT <= scrollTop + clientHeight,
  };
};

export const EnhancedCourseNavigation = ({
  backDisabled,
  onBackClick,
  onNextClick,
  nextHidden,
  editorContainerRef,
  isDrawerOpen,
  activePageId,
}: EnhancedCourseNavigationProps): JSX.Element | null => {
  const {
    reflectionInputRef,
    submitQuizButtonRef,
    submitQuizButtonHighlightRef,
    forceNavigationUpdateRef,
    hasReachedEndRef,
    isPageLoading,
    retakeQuizButtonRef,
    quizSubmitted,
    quizHasPassed,
    assignmentStep,
    assignmentStepActionRef,
  } = useContext(CourseNavigationContext);
  const [editorScrollTop, setEditorScrollTop] = useState(0);
  // Whether the learner scrolled the current page all the way down. Latched, so scrolling
  // back up afterwards still counts as having seen the end. This navigation stays mounted
  // while the learner moves between pages, so it is reset when the active page changes.
  const hasScrolledToEndRef = useRef(false);
  // Tricky trick to force component to rerender, used when we have some changes in ref's and
  // we need to force manual rerender.
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    (forceNavigationUpdateRef as { current: VoidFunction }).current = forceUpdate;
    forceUpdate();
  }, [forceUpdate]);

  // Keeps the shared "the learner reached the end of this page" flag in sync with what is on
  // screen. A Page activity completes on unmount, so this flag decides whether leaving the
  // page grants progress (see PT-5785). The end counts as reached when the learner scrolled
  // to the bottom, or when the content fits the viewport and there is nothing to scroll at
  // all. The latter is re-derived on every measurement instead of being latched: content
  // that grows after it loads (images, embeds) turns the page scrollable again, and then the
  // end has not been seen yet.
  const syncHasReachedEnd = useCallback(() => {
    const container = editorContainerRef?.current;
    const metrics = getEditorScrollMetrics(container, container?.scrollTop || 0);

    if (!hasReachedEndRef || !metrics) {
      return;
    }

    hasReachedEndRef.current = hasScrolledToEndRef.current || !metrics.isScrollable;
  }, [editorContainerRef, hasReachedEndRef]);

  useEffect(() => {
    const handleScroll = () => {
      const container = editorContainerRef?.current;
      const metrics = getEditorScrollMetrics(container, container?.scrollTop || 0);

      setEditorScrollTop(container?.scrollTop || 0);

      if (metrics?.isScrollable && metrics.isScrolledToBottom) {
        hasScrolledToEndRef.current = true;
      }

      syncHasReachedEnd();
    };

    if (editorContainerRef?.current) {
      editorContainerRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (editorContainerRef?.current) {
        editorContainerRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [editorContainerRef, syncHasReachedEnd]);

  // Per-page state: the learner has not seen the end of a page they just opened, and the
  // scroll offset belongs to the page they came from.
  useEffect(() => {
    hasScrolledToEndRef.current = false;
    setEditorScrollTop(editorContainerRef?.current?.scrollTop || 0);
  }, [activePageId, editorContainerRef]);

  // Page content keeps changing height after it is first measured - images and embeds load
  // late, the window gets resized - which flips a page between "fits on the screen" and
  // "has to be scrolled". Rerender on any such change so both the button state and the
  // end-of-page flag are based on the current layout.
  useEffect(() => {
    const content = editorContainerRef?.current?.firstElementChild;

    if (!content) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      forceUpdate();
    });

    resizeObserver.observe(content);

    return () => {
      resizeObserver.disconnect();
    };
  }, [editorContainerRef, forceUpdate]);

  // Intentionally runs after every render - remeasuring on the renders triggered by mounting
  // a page, by its content changing height and by scrolling is what keeps the flag correct
  // for pages that are never scrolled because they fit on the screen.
  useEffect(() => {
    syncHasReachedEnd();
  });

  const isEditorScrolledToBottom =
    getEditorScrollMetrics(editorContainerRef?.current, editorScrollTop)?.isScrolledToBottom ?? true;

  const handleRightButtonClick = () => {
    if (quizSubmitted && !quizHasPassed && !!retakeQuizButtonRef?.current?.focus) {
      retakeQuizButtonRef?.current?.focus();
      editorContainerRef?.current?.scrollTo({
        top: editorContainerRef?.current?.scrollHeight,
        behavior: 'smooth',
      });
      return;
    }
    // Assignment: scroll to the submission box, then let the page open the picker or highlight Submit
    if (assignmentStep) {
      editorContainerRef?.current?.scrollTo({
        top: editorContainerRef?.current?.scrollHeight,
        behavior: 'smooth',
      });
      assignmentStepActionRef?.current?.();
      return;
    }
    if (!!submitQuizButtonRef?.current?.focus) {
      editorContainerRef?.current?.scrollTo({
        top: editorContainerRef?.current?.scrollHeight,
        behavior: 'smooth',
      });

      submitQuizButtonHighlightRef?.current && submitQuizButtonHighlightRef.current();
    } else if (!!reflectionInputRef?.current?.focus) {
      reflectionInputRef?.current?.focus();

      editorContainerRef?.current?.scrollTo({
        top: editorContainerRef?.current?.scrollHeight,
        behavior: 'smooth',
      });
    } else if (!isEditorScrolledToBottom) {
      editorContainerRef?.current?.scrollTo({
        top: editorContainerRef?.current?.scrollHeight,
        behavior: 'smooth',
      });
    } else if (isEditorScrolledToBottom) {
      onNextClick();
    }
  };

  const getRightButtonState = () => {
    if (quizSubmitted && !quizHasPassed) {
      return {
        icon: ['far', 'arrows-repeat'],
        label: 'Scroll to retake',
      };
    } else if (assignmentStep === 'upload') {
      return {
        icon: ['far', 'cloud-arrow-up'],
        label: 'Upload file',
      };
    } else if (assignmentStep === 'submit') {
      return {
        icon: ['far', 'file-arrow-up'],
        label: 'Scroll to submit',
      };
    } else if (!!submitQuizButtonRef?.current?.focus) {
      return {
        icon: 'game-board-simple',
        label: 'Scroll to submit',
      };
    } else if (!!reflectionInputRef?.current?.focus) {
      return {
        icon: 'pen-line',
        label: 'Scroll to reflection',
      };
    } else if (!isEditorScrolledToBottom) {
      return {
        icon: 'arrow-down',
        label: 'Scroll down',
      };
    } else if (isEditorScrolledToBottom) {
      return {
        icon: 'arrow-right',
        label: 'Next',
      };
    }
  };

  const rightButtonState = getRightButtonState();

  if (isPageLoading) return null;

  const leftButtonShiftX = isDrawerOpen ? 345 : 0; // 20px base + 345px shift = 365px from left when open

  const activateOnKey = (handler: () => void) => (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handler();
    }
  };

  const rightButtonA11yProps = {
    role: 'button',
    tabIndex: 0,
    'aria-label': rightButtonState?.label,
    onKeyDown: activateOnKey(handleRightButtonClick),
  };

  const leftButtonA11yProps = {
    role: 'button',
    tabIndex: 0,
    'aria-label': 'Back',
    onKeyDown: activateOnKey(onBackClick),
  };

  return (
    <>
      {(!nextHidden || (quizSubmitted && !quizHasPassed)) && (
        <RightButtonStyled onClick={handleRightButtonClick} as={motion.div} layout {...rightButtonA11yProps}>
          <motion.div layout style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={String(rightButtonState?.icon)}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 4 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <IconStyled icon={rightButtonState?.icon as IconPropType} />
              </motion.div>
            </AnimatePresence>

            <AnimatePresence mode="wait">
              <motion.div
                key={rightButtonState?.label}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                layout
              >
                <LabelStyled>{rightButtonState?.label}</LabelStyled>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </RightButtonStyled>
      )}
      {!backDisabled && (
        <LeftButtonStyled onClick={onBackClick} $shiftX={leftButtonShiftX} {...leftButtonA11yProps}>
          <IconStyled icon="arrow-left" />
        </LeftButtonStyled>
      )}
    </>
  );
};
